import { useState, createContext, useContext, useEffect, useCallback, useRef } from "react";

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL || "";

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // True hvis backend rapporterer at brugerens rettigheder er ændret EFTER
  // JWT'en blev udstedt. UI viser banner og beder brugeren logge ud/ind.
  const [rettighederStale, setRettighederStale] = useState(false);
  const refreshTimer = useRef(null);
  const rettigPollTimer = useRef(null);

  const scheduleRefresh = useCallback((jwt) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    try {
      const payload = JSON.parse(atob(jwt.split(".")[1]));
      const expiresAt = payload.exp * 1000;
      // Refresh 1 hour before expiry
      const refreshIn = Math.max(expiresAt - Date.now() - 3600000, 60000);
      refreshTimer.current = setTimeout(() => refreshToken(jwt), refreshIn);
    } catch {}
  }, []);

  const refreshToken = useCallback(async (currentToken) => {
    try {
      const res = await fetch(`${API}/api/auth/refresh`, {
        method: "POST",
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem("ph_auth", JSON.stringify({ token: data.token, user: data.user }));
        scheduleRefresh(data.token);
      }
    } catch {}
  }, [scheduleRefresh]);

  useEffect(() => {
    // Check for impersonation token in URL (?token=...)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      try {
        const payload = JSON.parse(atob(urlToken.split(".")[1]));
        const authData = { token: urlToken, user: payload };
        sessionStorage.setItem("ph_auth", JSON.stringify(authData));
        setToken(urlToken);
        setUser(payload);
        // Remove token from URL without reload
        const clean = window.location.pathname + (params.toString().replace(/[?&]?token=[^&]+/, "").replace(/^&/, "?") || "");
        window.history.replaceState({}, "", clean);
        setLoading(false);
        return;
      } catch {}
    }

    const saved = localStorage.getItem("ph_auth") || sessionStorage.getItem("ph_auth");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setToken(parsed.token);
        setUser(parsed.user);
        scheduleRefresh(parsed.token);
      } catch {}
    }
    setLoading(false);
  }, [scheduleRefresh]);

  const login = (tokenValue, userData, remember = false) => {
    setToken(tokenValue);
    setUser(userData);
    if (remember) {
      localStorage.setItem("ph_auth", JSON.stringify({ token: tokenValue, user: userData }));
    } else {
      sessionStorage.setItem("ph_auth", JSON.stringify({ token: tokenValue, user: userData }));
      localStorage.removeItem("ph_auth");
    }
    scheduleRefresh(tokenValue);
  };

  const logout = () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    if (rettigPollTimer.current) clearInterval(rettigPollTimer.current);
    setToken(null);
    setUser(null);
    setRettighederStale(false);
    localStorage.removeItem("ph_auth");
    sessionStorage.removeItem("ph_auth");
  };

  // Poll backend hvert minut for at se om rettighederne er ændret efter JWT'ens iat.
  // Hvis ja, sæt rettighederStale = true så UI kan vise et banner.
  useEffect(() => {
    if (rettigPollTimer.current) {
      clearInterval(rettigPollTimer.current);
      rettigPollTimer.current = null;
    }
    if (!token) return;
    let iat = 0;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      iat = (payload.iat || 0) * 1000;
    } catch {
      return;
    }
    const check = async () => {
      try {
        const res = await fetch(`${API}/api/auth/rettigheder-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const aendret = data?.rettigheder_aendret_at;
        if (!aendret) return;
        const aendretMs = Date.parse(aendret);
        if (!isNaN(aendretMs) && aendretMs > iat) {
          setRettighederStale(true);
        }
      } catch {}
    };
    // Kør straks ved mount/token-ændring, derefter hvert minut.
    check();
    rettigPollTimer.current = setInterval(check, 60000);
    return () => {
      if (rettigPollTimer.current) {
        clearInterval(rettigPollTimer.current);
        rettigPollTimer.current = null;
      }
    };
  }, [token]);

  // Opt-in-rettigheder: bypasser IKKE admin auto-grant. Både admin og members
  // skal have rettigheden eksplicit tildelt. Holdes synkroniseret med
  // backend.OPT_IN_RETTIGHED_IDS.
  const OPT_IN_RETTIGHEDER_LOCAL = new Set([
    "kan_se_fortrolige_medarbejderoplysninger",
  ]);

  // Hjælper: konsekvent tjek af role + individuelle rettigheder.
  // - Superadmin/Owner har ALTID alle rettigheder automatisk (inkl. opt-in).
  // - Admin har alle rettigheder AUTOMATISK undtagen opt-in-rettigheder
  //   (fx kan_se_fortrolige_medarbejderoplysninger).
  // - Medarbejder (member) har kun de eksplicit tildelte (fra user.rettigheder).
  const hasRettighed = (navn) => {
    if (!user) return false;
    // api_admin: skrivebeskyttet, men ser alt som superadmin. Skrive-knapper
    // skjules separat via erSkrivebeskyttet/role-tjek; backend afviser writes.
    if (["superadmin", "owner", "api_admin"].includes(user.role)) return true;
    if (user.role === "admin" && !OPT_IN_RETTIGHEDER_LOCAL.has(navn)) return true;
    const r = user.rettigheder || {};
    return !!r[navn];
  };

  // api_admin: skrivebeskyttet super-rolle. Backend afviser alle writes (403);
  // her bruges flaget kun til at vise et diskret "skrivebeskyttet"-banner.
  const erSkrivebeskyttet = user?.role === "api_admin";

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, hasRettighed, rettighederStale, erSkrivebeskyttet }}>
      {children}
      {erSkrivebeskyttet && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0,
            zIndex: 9998, padding: "5px 12px",
            background: "#475569", color: "#fff",
            textAlign: "center", fontSize: 12, fontWeight: 600,
            letterSpacing: 0.2,
          }}
        >
          API admin · skrivebeskyttet adgang (kun læsning)
        </div>
      )}
      {rettighederStale && user && (
        <div
          role="alert"
          style={{
            position: "fixed", left: 0, right: 0, bottom: 0,
            zIndex: 9999, padding: "12px 16px",
            background: "#FF2A6D", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 12, fontSize: 14, fontWeight: 600,
            boxShadow: "0 -4px 16px rgba(0,0,0,0.2)",
          }}
        >
          <span>Dine rettigheder er opdateret. Log ud og ind igen for at se ændringerne.</span>
          <button
            onClick={logout}
            style={{
              background: "#fff", color: "#FF2A6D",
              border: "none", padding: "6px 14px",
              borderRadius: 9999, fontWeight: 700, cursor: "pointer",
              fontSize: 13,
            }}
          >
            Log ud nu
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
};
